import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { DomainBioscopeDay, DomainBioscopeDayMetrics } from '@/domain/cages/overview.types';
import type { Locale } from '@/utils/localeFormatters';

import { daypartMetricSeries } from '../daypartSeries';

import type { DaypartChartSpecParams } from './types';

// ---------------------------------------------------------------------------
// Bioscope attitude — pitch and roll (admin only)
// ---------------------------------------------------------------------------

export interface PitchRollChartSpecParams {
    days: DomainBioscopeDay[];
    /** All already translated. */
    axisLabel: string;
    pitchLabel: string;
    rollLabel: string;
    locale: Locale;
}

const ANGLE_UNIT = '°';
const ANGLE_TICK_DECIMALS = 1;
const ANGLE_VALUE_DECIMALS = 2;
/** The legacy mapper hardcoded this daypart, ignoring the page filter. */
const PITCH_ROLL_DAYPART_TAG = 'day';

/**
 * Bioscope pitch and roll, in degrees.
 *
 * Diagnostic rather than biological: it shows whether the camera is hanging
 * level. Always plotted for the `day` daypart, as the legacy mapper did.
 */
export function buildPitchRollChartSpec(params: PitchRollChartSpecParams): MetricChartSpec<'line'> {
    const { days, axisLabel, pitchLabel, rollLabel, locale } = params;
    const attitudeDays = days.filter((day) => day.daypartTag === PITCH_ROLL_DAYPART_TAG);

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: [
            {
                key: 'bioscopePitchAvg',
                label: pitchLabel,
                color: graphSettings.colors.primary,
                data: attitudeDays.map((day) => ({
                    day: day.day,
                    value: day.data.bioscopePitchAvg,
                })),
            },
            {
                key: 'bioscopeRollAvg',
                label: rollLabel,
                color: graphSettings.colors.secondary,
                data: attitudeDays.map((day) => ({
                    day: day.day,
                    value: day.data.bioscopeRollAvg,
                })),
            },
        ],
        left: {
            label: axisLabel,
            unit: ANGLE_UNIT,
            beginAtZero: true,
            tickDecimals: ANGLE_TICK_DECIMALS,
            valueDecimals: ANGLE_VALUE_DECIMALS,
        },
        locale,
    };
}

// ---------------------------------------------------------------------------
// Vertical disparity (admin only)
// ---------------------------------------------------------------------------

export interface VerticalDisparityChartSpecParams
    extends DaypartChartSpecParams<DomainBioscopeDayMetrics> {
    axisLabel: string;
}

const DISPARITY_UNIT = 'px';
const DISPARITY_SUGGESTED_MIN = -1;
const DISPARITY_SUGGESTED_MAX = 1;

/**
 * Vertical disparity between the stereo pair, in pixels.
 *
 * A calibration diagnostic: a healthy camera sits near zero, so the axis is
 * suggested symmetric around it rather than fitted to the data.
 */
export function buildVerticalDisparityChartSpec(
    params: VerticalDisparityChartSpecParams
): MetricChartSpec<'line'> {
    const { days, graphedDaypartTags, activeDaypartTags, daypartLabel, locale, axisLabel } = params;

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: daypartMetricSeries({
            days,
            metric: 'verticalDisparity',
            graphedDaypartTags,
            activeDaypartTags,
            label: daypartLabel,
            keyPrefix: 'verticalDisparity',
        }),
        left: {
            label: axisLabel,
            unit: DISPARITY_UNIT,
            suggestedMin: DISPARITY_SUGGESTED_MIN,
            suggestedMax: DISPARITY_SUGGESTED_MAX,
            tickDecimals: 1,
            valueDecimals: 2,
        },
        locale,
    };
}
